import { Trans } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Switch } from '@startuptools/ui/Switch';
import { MenuItem } from '@startuptools/ui/MenuItem';
import { Menu } from '@startuptools/ui/Menu';
import { MenuButton } from '@startuptools/ui/MenuButton';
import { Dropdown } from '@startuptools/ui/Dropdown';
import { FormControl } from '@startuptools/ui/FormControl';
import { Divider } from '@startuptools/ui/Divider';
import { Sheet } from '@startuptools/ui/Sheet';
import { useState, type ReactNode } from 'react';
import { SccsDefaultTable } from './SccsDefaultTable';
import { SccsExtendedTable } from './SccsExtendedTable';
import { SccsSplitAdjustedTable } from './SccsSplitAdjustedTable';
import { type ApolloError } from '@apollo/client';
import { SccsNoFoundationTable } from './SccsNoFoundationTable';
import { downloadSccsCsv } from '../utils/download-csv';
import { GqlCurrency, type GqlCompleteShareCapitalChange } from '../../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/Button';
import { useCurrentUserSuspenseQuery } from '../../../graphql/react-operations';
import { useRefreshCompanyMutation } from './SccsTableInner.graphql';
import { SccsTableDocument } from './SccsTable.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Chip } from '@startuptools/ui/Chip';

const SccsStack = ({
  children,
  toggles,
  handleLanguageSelect,
}: {
  children: ReactNode;
  toggles: ReactNode;
  handleLanguageSelect: (language: string) => void;
}) => {
  const companyId = useCompanyId();
  const {
    data: { currentUser },
  } = useCurrentUserSuspenseQuery();
  const [doRefresh, { loading }] = useRefreshCompanyMutation({ refetchQueries: [SccsTableDocument] });

  return (
    <Stack gap={1}>
      <Typography level="h2">
        <Trans id="Share Capital Changes" />
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        {toggles}

        <Stack direction="row" gap={1}>
          {currentUser.admin && (
            <Button
              variant="outlined"
              color="neutral"
              loading={loading}
              onClick={() => doRefresh({ variables: { companyId } })}
              endDecorator={
                <Chip size="sm" variant="outlined">
                  admin
                </Chip>
              }
            >
              <Trans id="Refresh Company" />
            </Button>
          )}
          <Dropdown>
            <MenuButton>
              <Trans id="Export as CSV" />
            </MenuButton>
            <Menu id="export-menu">
              <MenuItem onClick={() => handleLanguageSelect('sv')}>
                <Trans id="Swedish" />
              </MenuItem>
              <MenuItem onClick={() => handleLanguageSelect('en')}>
                <Trans id="English" />
              </MenuItem>
            </Menu>
          </Dropdown>
        </Stack>
      </Stack>
      {children}
    </Stack>
  );
};

export const SccsTableInner = ({
  rows,
  error,
  totalPaid,
  hasFoundation,
  companyName,
}: {
  rows: GqlCompleteShareCapitalChange[];
  error: ApolloError | undefined;
  totalPaid: { [k in GqlCurrency]?: number };
  hasFoundation: boolean;
  companyName: string;
}) => {
  const [isExtendedViewChecked, setIsExtendedViewChecked] = useState(false);
  const [isSplitAdjustedChecked, setIsSplitAdjustedChecked] = useState(false);

  const handleLanguageSelect = (language: string) => {
    downloadSccsCsv(language, rows, companyName, hasFoundation);
  };

  let toggles = null;
  if (hasFoundation) {
    toggles = (
      <Stack direction="row" gap={2} alignItems="center">
        <FormControl>
          <Switch
            checked={isExtendedViewChecked}
            onChange={event => {
              if (!event.target.checked) {
                setIsSplitAdjustedChecked(false);
              }
              setIsExtendedViewChecked(event.target.checked);
            }}
            endDecorator={<Trans id="Extended view" />}
          ></Switch>
        </FormControl>
        <Divider orientation="vertical" />
        <FormControl disabled={!isExtendedViewChecked}>
          <Switch
            checked={isSplitAdjustedChecked}
            onChange={event => setIsSplitAdjustedChecked(event.target.checked)}
            endDecorator={<Trans id="Split adjusted" />}
          ></Switch>
        </FormControl>
      </Stack>
    );
  }

  let tables;
  if (error) {
    tables = (
      <Alert variant="outlined" color="danger">
        <Trans id="An error occurred. Please refresh the page. If the error persists, contact our customer support" />
      </Alert>
    );
  } else if (!hasFoundation) {
    tables = <SccsNoFoundationTable totalPaid={totalPaid} rows={rows} />;
  } else {
    tables = (
      <Sheet variant="outlined" sx={{ overflowX: 'auto' }}>
        {isExtendedViewChecked ? (
          isSplitAdjustedChecked ? (
            <SccsSplitAdjustedTable rows={rows} totalPaid={totalPaid} />
          ) : (
            <SccsExtendedTable rows={rows} totalPaid={totalPaid} />
          )
        ) : (
          <SccsDefaultTable rows={rows} totalPaid={totalPaid} />
        )}
      </Sheet>
    );
  }
  return (
    <SccsStack toggles={toggles} handleLanguageSelect={handleLanguageSelect}>
      {tables}
    </SccsStack>
  );
};
